import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { GiftCategoryLocalized } from './giftStoreCategories';
import { trackCategoryViewed } from './giftStoreAnalytics';

interface CategoryCardProps {
  category: GiftCategoryLocalized;
  isSelected: boolean;
  onSelect: (slug: string) => void;
  index: number;
  showingLabel?: string;
}

export function CategoryCard({ category, isSelected, onSelect, index, showingLabel }: CategoryCardProps) {
  const { Icon, title, description, slug, explore } = category;

  const handleClick = () => {
    onSelect(isSelected ? '' : slug);
    if (!isSelected) {
      trackCategoryViewed(slug, title);
    }
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onClick={handleClick}
      className={cn(
        'group w-full text-left flex flex-col gap-3 p-5 rounded-2xl border transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected
          ? 'border-primary bg-primary/8 shadow-md -translate-y-0.5'
          : 'border-border bg-card hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-md',
      )}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
          isSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary group-hover:bg-primary/20',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h3
          className={cn(
            'font-semibold text-sm leading-tight mb-1',
            isSelected ? 'text-primary' : 'text-foreground',
          )}
        >
          {title}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <span
        className={cn(
          'text-xs font-medium mt-auto transition-colors',
          isSelected ? 'text-primary' : 'text-muted-foreground group-hover:text-primary',
        )}
      >
        {isSelected ? (showingLabel || '✓ Showing results') : explore}
      </span>
    </motion.button>
  );
}
